import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { StepItems } from 'src/app/shared/components/stepper/stepper.component';
import { CREATE_POSITION_STEPS } from './create-position.proxy';
import { CreatePositionActions } from './index';

export const createPositionFeatureKey = 'createPosition';
export interface CreatePositionState {
  vacancyId: string;
  descriptionPrompt: string;
  status: string;
  steps: StepItems[];
  currentStep: number;
  jobTitles: { id: string, name: string, isCustomJobTitle: boolean }[];
  jobPosition: string;
  jobPositions: string[];
  jobPositionLevel: string;
  hardSkills: any;
  softSkills: any;
  additionalInfo: any;
  isValidationRequired: boolean;
  assessments: number[];
  forceHighAccuracy: boolean;
}

export interface HardSkills {
  id: number;
  label: string,
  subSkills: {
    id: number;
    label: string;
    experience: number;
    years: number;
  }[] | null;
}

const initialState: CreatePositionState = {
  vacancyId: null, //'63f3ae64281cd43fe64e58b0', 63f3c126281cd43fe64e58b1
  descriptionPrompt: null,
  status: null,
  steps: [],
  currentStep: null,
  jobTitles: null,
  jobPosition: null,
  jobPositions: null,
  jobPositionLevel: null,
  hardSkills: null,
  softSkills: null,
  additionalInfo: null,
  isValidationRequired: false,
  assessments: [],
  forceHighAccuracy: false
}

const reducer = createReducer<CreatePositionState>(
  initialState,
  on(CreatePositionActions.SetVacancyId, (state, action) => ({ ...state, vacancyId: action.vacancyId })),
  on(CreatePositionActions.SetSeniority, (state, action) => ({ ...state, jobPositionLevel: action.level })),
  on(CreatePositionActions.SetSteps, (state, action) => ({ 
    ...state,
    steps: action.steps, 
    currentStep: action.currentStep 
  })),
  on(CreatePositionActions.SetCurrentStep, (state, action) => ({ ...state, currentStep: action.currentStep })),
  on(CreatePositionActions.GetJobTitlesSuccess, (state, action) => ({ ...state, jobTitles: action.jobTitles })),
  on(CreatePositionActions.GetVacancySuccess, (state, { vacancy }) => ({...state,vacancy,isValidationRequired: vacancy.isValidationRequired, 
})),
  on(CreatePositionActions.SetRequireValidation, (state, { value }) => {return {
    ...state,
    isValidationRequired: value
  };
}),


  on(CreatePositionActions.SetAdditionalJobPositions, (state, action) => ({ ...state, jobPositions: action.jobPositions })),
  on(CreatePositionActions.StepValid, (state, action) => {
    const toReturn = {
      ...state,
      steps: state.steps.map(item => (item.key === action.key ? { ...item, isComplited: true } : item)),
      currentStep: state.steps.length > state.currentStep + 1 ? state.currentStep + 1 : state.currentStep,
      [action.key]: action.data ? action.data : state[action.key]
    };
    return toReturn;
  }),
  on(CreatePositionActions.GetVacancySuccess, (state, action) => {
    const toReturn = {
      ...state,
      ...action.vacancy
    };
    return toReturn;
  }),
  on(CreatePositionActions.SetHardSkills, (state, action) => ({ 
    ...state,
    hardSkills: action.skillsList
  })),
  on(CreatePositionActions.SetSoftSkills, (state, action) => ({ 
    ...state,
    softSkills: action.skillsList
  })),
  on(CreatePositionActions.GetSoftSkillsSuccess, (state, action) => ({ 
    ...state,
    softSkills: action.skillsList ? action.skillsList : state.softSkills
  })),
  on(CreatePositionActions.SetCountry, (state, action) => ({ 
    ...state,
    additionalInfo: {
      ...state.additionalInfo,
      country: action.country
    }
  })),
  on(CreatePositionActions.SetCurrency, (state, action) => ({ 
    ...state,
    additionalInfo: {
      ...state.additionalInfo,
      salaryCurrency: action.currency
    }
  })),
  on(CreatePositionActions.DropVacancy, (state, action) => ({ 
    ...state,
    steps: [],
    status: null,
    vacancyId: null,
    descriptionPrompt: null,
    currentStep: 0,
    jobPosition: null,
    jobPositions: [],
    jobPositionLevel: null,
    hardSkills: null,
    softSkills: null,
    additionalInfo: null,
    assessments: []
  }))
);

export function createPositionReducer(state: CreatePositionState | undefined, action: Action) {
  return reducer(state, action);
}

export const selectCreatePositionState = createFeatureSelector<CreatePositionState>(createPositionFeatureKey);

export const selectVacancyId = createSelector(
  selectCreatePositionState,
  (state) => state.vacancyId
);

export const selectDescriptionPrompt = createSelector(
  selectCreatePositionState,
  (state) => state.descriptionPrompt
);

export const selectStepsState = createSelector(
  selectCreatePositionState,
  (state) => state.steps
);

export const selectCurrentSteps = createSelector(
  selectCreatePositionState,
  (state) => state.currentStep
);

export const selectJobTitles = createSelector(
  selectCreatePositionState,
  (state) => state.jobTitles
);

export const selectJobPosition = createSelector(
  selectCreatePositionState,
  (state) => state.jobPosition
);

export const selectJobPositions = createSelector(
  selectCreatePositionState,
  (state) => state.jobPositions
);

export const selectJobPositionLevel = createSelector(
  selectCreatePositionState,
  (state) => state.jobPositionLevel
);

export const selectHardSkills = createSelector(
  selectCreatePositionState,
  (state) => state.hardSkills
);

export const selectSoftSkills = createSelector(
  selectCreatePositionState,
  (state) => state.softSkills
);

export const selectAdditionalInfo = createSelector(
  selectCreatePositionState,
  (state) => state.additionalInfo
);

export const selectVacancyStatus = createSelector(
  selectCreatePositionState,
  (state) => state.status
);

export const selectRequireValidation = createSelector(
    selectCreatePositionState,
  (state: CreatePositionState) => state.isValidationRequired
);


export const selectSalaryRangeParams = createSelector(
  selectCreatePositionState,
  (state) => ({
    country: state?.additionalInfo?.country ? state.additionalInfo.country : null,
    position: state?.jobPosition ? state.jobPosition : null,
    seniority: state?.jobPositionLevel ? state.jobPositionLevel : null,
    currency: state?.additionalInfo?.salaryCurrency ? state.additionalInfo.salaryCurrency : null
  })
);

export const selectAssessments = createSelector(
  selectCreatePositionState,
  (state) => state.assessments || []
);

export const selectForceHighAccuracy = createSelector(
  selectCreatePositionState,
  (state) => state.forceHighAccuracy
);

